/**
 * Performance Metrics Component
 * Displays portfolio performance statistics and top performers
 */

import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { PerformanceMetrics as PerformanceMetricsType, PortfolioConfig } from '../types/portfolio';
import { Card } from './Card';

interface PerformanceMetricsProps {
  performance: PerformanceMetricsType;
  config: PortfolioConfig;
}

export const PerformanceMetrics: React.FC<PerformanceMetricsProps> = ({ 
  performance, 
  config 
}) => {
  const { t } = useTranslation();
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: config.baseCurrency,
      minimumFractionDigits: config.displayPrecision,
      maximumFractionDigits: config.displayPrecision,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const getChangeColor = (value: number) => {
    if (value > 0) return 'text-green-600 dark:text-green-400';
    if (value < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const getChangeIcon = (value: number) => {
    if (value > 0) {
      return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17l9.2-9.2M17 17V7H7" />
        </svg>
      );
    }
    if (value < 0) {
      return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 7l-9.2 9.2M7 7v10h10" />
        </svg>
      );
    }
    return null;
  };

  const performanceMetrics = [
    {
      label: t('portfolio.totalReturn'),
      value: performance.totalReturn,
      percentage: performance.totalReturnPercent,
      delay: 0.1,
    },
    {
      label: t('portfolio.dayReturn'),
      value: performance.dayReturn,
      percentage: performance.dayReturnPercent,
      delay: 0.2,
    },
    // Note: Week, Month, Year returns would need historical data to be accurate
    // For now, they're set to 0 in the store
  ];

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
        {t('portfolio.performanceMetrics')}
      </h3>

      <div className="space-y-4">
        {/* Performance Returns */}
        {performanceMetrics.map((metric) => (
          <motion.div
            key={metric.label}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: metric.delay }}
            className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50"
          >
            <div className="font-medium text-gray-900 dark:text-white">
              {metric.label}
            </div>
            <div className="text-right">
              <div className={`text-lg font-semibold ${getChangeColor(metric.value)}`}>
                {formatCurrency(metric.value)}
              </div>
              {config.showPercentages && (
                <div className={`text-sm flex items-center gap-1 justify-end ${getChangeColor(metric.value)}`}>
                  {getChangeIcon(metric.value)}
                  {formatPercentage(metric.percentage)}
                </div>
              )}
            </div>
          </motion.div>
        ))}

        {/* Top Performers */}
        {(performance.bestPerformer || performance.worstPerformer) && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4">
              {t('portfolio.topPerformers')}
            </h4>
            
            <div className="space-y-3">
              {/* Best Performer */}
              {performance.bestPerformer && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex items-center justify-between p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {performance.bestPerformer.asset.symbol}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {t('portfolio.bestPerformer')}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                      {formatCurrency(performance.bestPerformer.unrealizedPnL)}
                    </div>
                    <div className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1 justify-end">
                      {getChangeIcon(performance.bestPerformer.unrealizedPnL)}
                      {formatPercentage(performance.bestPerformer.unrealizedPnLPercent)}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Worst Performer */}
              {performance.worstPerformer && performance.worstPerformer.unrealizedPnL < 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="flex items-center justify-between p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {performance.worstPerformer.asset.symbol}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {t('portfolio.worstPerformer')}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-red-600 dark:text-red-400">
                      {formatCurrency(performance.worstPerformer.unrealizedPnL)}
                    </div>
                    <div className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1 justify-end">
                      {getChangeIcon(performance.worstPerformer.unrealizedPnL)}
                      {formatPercentage(performance.worstPerformer.unrealizedPnLPercent)}
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!performance.bestPerformer && !performance.worstPerformer && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="text-center text-gray-500 dark:text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-sm">{t('portfolio.addHoldingsToSeeMetrics')}</p>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};